import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { 
  Plus, MessageSquare, Heart, ThumbsUp, HandHeart, Lightbulb, Sparkles, PartyPopper, BookmarkPlus, Send, User, Calendar, 
  ChevronRight, ArrowLeft, Trash2, Flag, EyeOff, Eye, Shield, Edit2
} from "lucide-react";
import { format, parseISO } from "date-fns";

const CATEGORIES = ["general", "autism", "adhd", "speech", "sensory", "education", "resources", "success_stories"];

const REACTIONS = [
  { type: "heart", Icon: Heart, label: "Love", color: "text-pink-600 fill-pink-600" },
  { type: "thumbs", Icon: ThumbsUp, label: "Helpful", color: "text-blue-500 fill-blue-500" },
  { type: "hug", Icon: HandHeart, label: "Hug", color: "text-orange-500 fill-orange-200" },
  { type: "insight", Icon: Lightbulb, label: "Insightful", color: "text-amber-500 fill-amber-300" },
  { type: "support", Icon: Sparkles, label: "Support", color: "text-purple-500 fill-purple-300" },
  { type: "celebrate", Icon: PartyPopper, label: "Celebrate", color: "text-emerald-500 fill-emerald-200" }
];

// Extract first URL from text (for "Save to Mycyclopedia" button)
const extractUrl = (text = "") => {
  const m = text.match(/\bhttps?:\/\/[^\s<>"']+/i);
  return m ? m[0] : null;
};

function ReactionBar({ post, onReact, onSave, commentCount }) {
  const { user } = useAuth();
  const reactions = post.reactions || [];
  const counts = REACTIONS.reduce((acc, r) => {
    acc[r.type] = reactions.filter(x => x.type === r.type).length;
    return acc;
  }, {});
  const myReaction = reactions.find(r => r.user_id === user?.id)?.type;
  const hasUrl = !!extractUrl(post.content) || !!extractUrl(post.title);

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-4 border-t border-border" data-testid={`reaction-bar-${post.id}`}>
      {REACTIONS.map(r => {
        const active = myReaction === r.type;
        const count = counts[r.type];
        return (
          <Button
            key={r.type}
            variant="ghost"
            size="sm"
            onClick={() => onReact(post.id, r.type)}
            className={`rounded-full h-9 px-3 ${active ? "bg-primary/10 text-primary" : ""}`}
            title={r.label}
            data-testid={`reaction-${r.type}-${post.id}`}
          >
            <r.Icon className={`h-4 w-4 ${active ? r.color : ""}`} />
            {count > 0 && <span className="ml-1.5 text-xs font-semibold">{count}</span>}
          </Button>
        );
      })}
      <div className="ml-auto flex items-center gap-3">
        <span className="flex items-center gap-1 text-sm text-muted-foreground">
          <MessageSquare className="h-4 w-4" />
          {commentCount ?? post.comment_count ?? 0}
        </span>
        {hasUrl && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSave(post)}
            className="rounded-full h-9"
            data-testid={`save-to-library-${post.id}`}
          >
            <BookmarkPlus className="mr-1.5 h-3.5 w-3.5" /> Save link
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CommunityPage() {
  const { user } = useAuth();
  const confirm = useConfirmation();
  const isMod = !!user?.is_moderator;
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [newComment, setNewComment] = useState("");
  const [reportTarget, setReportTarget] = useState(null); // { type: 'post'|'comment', id, title? }
  const [reportReason, setReportReason] = useState("");
  const [modAction, setModAction] = useState(null); // { targetType, target, action, editContent?, editTitle? }
  const [modReason, setModReason] = useState("");
  const [form, setForm] = useState({
    title: "",
    content: "",
    category: "general"
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await apiClient.get("/posts");
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to load posts");
    }
    setLoading(false);
  };

  const fetchComments = async (postId) => {
    try {
      const response = await apiClient.get(`/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error("Failed to load comments");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/posts", form);
      toast.success("Post created!");
      fetchPosts();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    }
  };

  const handleReaction = async (postId, type) => {
    try {
      const res = await apiClient.post(`/posts/${postId}/reactions`, { type });
      // Update both list and selected post in place
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, reactions: res.data.reactions } : p));
      if (selectedPost?.id === postId) setSelectedPost(prev => ({ ...prev, reactions: res.data.reactions }));
    } catch (error) {
      toast.error("Failed to react");
    }
  };

  const handleSaveToLibrary = async (post) => {
    const url = extractUrl(post.content) || extractUrl(post.title);
    if (!url) {
      toast.error("This post has no link to save");
      return;
    }
    try {
      await apiClient.post("/mycyclopedia", { url, tags: [post.category].filter(Boolean), source_post_id: post.id });
      toast.success("Saved to your Mycyclopedia");
    } catch (err) {
      if (err.response?.data?.already_saved) {
        toast.info("Already in your library");
      } else {
        toast.error(err.response?.data?.detail || "Could not save");
      }
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedPost) return;
    try {
      await apiClient.post("/comments", {
        post_id: selectedPost.id,
        content: newComment
      });
      toast.success("Comment added!");
      setNewComment("");
      fetchComments(selectedPost.id);
      fetchPosts(); // Refresh comment count
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!(await confirm({ title: "Delete post?", description: "This post and its comments will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/posts/${postId}`);
      toast.success("Post deleted");
      fetchPosts();
      if (selectedPost?.id === postId) setSelectedPost(null);
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const submitReport = async () => {
    if (!reportTarget) return;
    const url = reportTarget.type === "post"
      ? `/posts/${reportTarget.id}/report`
      : `/comments/${reportTarget.id}/report`;
    try {
      await apiClient.post(url, { reason: reportReason });
      toast.success("Thanks — a moderator will review this soon.");
      setReportTarget(null);
      setReportReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send report");
    }
  };

  const openModAction = (targetType, target, action) => {
    setModReason("");
    setModAction({
      targetType,
      target,
      action,
      editContent: target.content || "",
      editTitle: target.title || ""
    });
  };

  const applyModAction = async () => {
    if (!modAction) return;
    const { targetType, target, action, editContent, editTitle } = modAction;
    const url = targetType === "post"
      ? `/moderation/posts/${target.id}`
      : `/moderation/comments/${target.id}`;
    const body = { action, reason: modReason };
    if (action === "edit") {
      body.edit = { content: editContent };
      if (targetType === "post") body.edit.title = editTitle;
    }
    try {
      await apiClient.post(url, body);
      toast.success(`Moderator ${action} applied`);
      setModAction(null);
      if (action === "delete" && targetType === "post" && selectedPost?.id === target.id) {
        setSelectedPost(null);
      } else if (targetType === "post" && selectedPost?.id === target.id) {
        // Refresh selectedPost so hidden/mod-edited banners + content update immediately in the detail view.
        try {
          const fresh = await apiClient.get(`/posts/${target.id}`);
          if (fresh.data) setSelectedPost(fresh.data);
        } catch { /* leave stale rather than crash */ }
      }
      fetchPosts();
      if (selectedPost) fetchComments(selectedPost.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await apiClient.delete(`/comments/${commentId}`);
      toast.success("Comment deleted");
      fetchComments(selectedPost.id);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const openPost = async (post) => {
    setSelectedPost(post);
    await fetchComments(post.id);
  };

  const resetForm = () => {
    setForm({ title: "", content: "", category: "general" });
    setDialogOpen(false);
  };

  const getCategoryColor = (cat) => {
    const colors = {
      general: "bg-muted text-muted-foreground",
      autism: "bg-teal-100 text-teal-700",
      adhd: "bg-golden-100 text-golden-500",
      speech: "bg-coral-100 text-coral-500",
      sensory: "bg-info/10 text-info",
      education: "bg-purple-100 text-purple-600",
      resources: "bg-success/10 text-success",
      success_stories: "bg-pink-100 text-pink-600"
    };
    return colors[cat] || "bg-muted text-muted-foreground";
  };

  const filteredPosts = filter === "all" 
    ? posts 
    : posts.filter(p => p.category === filter);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  // Shared moderation dialogs — must be rendered in BOTH the post-list and post-detail
  // views so the report/mod-action buttons work regardless of which view is active.
  const communityDialogs = (
    <>
      {/* Report dialog (all users) */}
      <Dialog open={!!reportTarget} onOpenChange={(o) => { if (!o) { setReportTarget(null); setReportReason(""); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Flag className="h-5 w-5 text-destructive" />
              Report {reportTarget?.type}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              Help us keep the community safe. What&apos;s wrong with this {reportTarget?.type}?
            </p>
            {reportTarget?.title && (
              <p className="text-xs text-muted-foreground italic">Reporting: &quot;{reportTarget.title}&quot;</p>
            )}
            <Textarea
              rows={4}
              className="rounded-xl"
              placeholder="e.g. Spam, harassment, misinformation, inappropriate content…"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              data-testid="report-reason-input"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setReportTarget(null)} className="flex-1 rounded-full">Cancel</Button>
            <Button onClick={submitReport} className="flex-1 rounded-full" data-testid="submit-report-btn">
              <Flag className="mr-2 h-4 w-4" />
              Send report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Moderator action dialog */}
      <Dialog open={!!modAction} onOpenChange={(o) => { if (!o) setModAction(null); }}>
        <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="community-mod-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading capitalize flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {modAction?.action} {modAction?.targetType}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {modAction?.action === "edit" && modAction?.targetType === "post" && (
              <div>
                <label className="text-xs font-medium mb-1 block">Title</label>
                <Input
                  className="rounded-xl"
                  value={modAction.editTitle}
                  onChange={(e) => setModAction(prev => ({ ...prev, editTitle: e.target.value }))}
                  data-testid="community-mod-edit-title"
                />
              </div>
            )}
            {modAction?.action === "edit" && (
              <div>
                <label className="text-xs font-medium mb-1 block">Content</label>
                <Textarea
                  rows={5}
                  className="rounded-xl"
                  value={modAction.editContent}
                  onChange={(e) => setModAction(prev => ({ ...prev, editContent: e.target.value }))}
                  data-testid="community-mod-edit-content"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium mb-1 block">Reason (visible in audit log)</label>
              <Textarea
                rows={2}
                className="rounded-xl"
                placeholder="Why are you taking this action?"
                value={modReason}
                onChange={(e) => setModReason(e.target.value)}
                data-testid="community-mod-reason-input"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setModAction(null)} className="flex-1 rounded-full">Cancel</Button>
            <Button onClick={applyModAction} className="flex-1 rounded-full" data-testid="community-mod-confirm-btn">
              Confirm {modAction?.action}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  // Post Detail View
  if (selectedPost) {
    return (
      <div className="space-y-6" data-testid="post-detail">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedPost(null)}
          className="rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Community
        </Button>

        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="p-6">
            {selectedPost.hidden && (
              <div className="mb-4 rounded-2xl border border-orange-300 bg-orange-50 px-4 py-3 flex items-start gap-3" data-testid="post-hidden-banner">
                <EyeOff className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-orange-900">Hidden by moderator</p>
                  {selectedPost.hidden_reason && <p className="text-orange-800/80 mt-0.5">Reason: {selectedPost.hidden_reason}</p>}
                  <p className="text-xs text-orange-700/70 mt-1">Only you and moderators can see this post right now.</p>
                </div>
              </div>
            )}
            {selectedPost.moderator_edited && (
              <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 flex items-center gap-2 text-xs text-blue-800" data-testid="post-mod-edited-banner">
                <Shield className="h-3.5 w-3.5" />
                Edited by a moderator{selectedPost.moderator_edit_reason ? ` — ${selectedPost.moderator_edit_reason}` : ""}
              </div>
            )}
            <div className="flex items-start gap-4 mb-4">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-primary/10 text-primary font-bold">
                  {selectedPost.user_name?.charAt(0).toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="font-heading font-bold text-2xl mb-2">{selectedPost.title}</h2>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{selectedPost.user_name}</span>
                  <Badge className={`rounded-full capitalize ${getCategoryColor(selectedPost.category)}`}>
                    {selectedPost.category.replace("_", " ")}
                  </Badge>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {format(parseISO(selectedPost.created_at), "MMM d, yyyy")}
                  </span>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {user?.id !== selectedPost.user_id && (
                  <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 text-muted-foreground" title="Report post"
                    onClick={() => setReportTarget({ type: "post", id: selectedPost.id, title: selectedPost.title })}
                    data-testid={`report-post-${selectedPost.id}`}>
                    <Flag className="h-4 w-4" />
                  </Button>
                )}
                {isMod && (
                  <>
                    {selectedPost.hidden ? (
                      <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 text-primary" title="Unhide"
                        onClick={() => openModAction("post", selectedPost, "unhide")}
                        data-testid={`mod-unhide-post-${selectedPost.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 text-primary" title="Hide (moderator)"
                        onClick={() => openModAction("post", selectedPost, "hide")}
                        data-testid={`mod-hide-post-${selectedPost.id}`}>
                        <EyeOff className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 text-primary" title="Edit (moderator)"
                      onClick={() => openModAction("post", selectedPost, "edit")}
                      data-testid={`mod-edit-post-${selectedPost.id}`}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 text-destructive" title="Delete (moderator)"
                      onClick={() => openModAction("post", selectedPost, "delete")}
                      data-testid={`mod-delete-post-${selectedPost.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            <p className="text-lg leading-relaxed whitespace-pre-wrap mb-6">{selectedPost.content}</p>
            
            <ReactionBar post={selectedPost} onReact={handleReaction} onSave={handleSaveToLibrary} commentCount={comments.length} />
          </CardContent>
        </Card>

        {/* Comments */}
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardHeader>
            <CardTitle className="font-heading">Comments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No comments yet. Be the first to share your thoughts!
              </p>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className={`flex gap-3 p-3 rounded-2xl ${comment.hidden ? "bg-orange-50 border border-orange-200" : "bg-muted/30"}`} data-testid={`comment-${comment.id}`}>
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                      {comment.user_name?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-sm">{comment.user_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {format(parseISO(comment.created_at), "MMM d, yyyy")}
                      </span>
                      {comment.hidden && (
                        <Badge variant="outline" className="rounded-full text-[10px] text-orange-700 border-orange-300">
                          <EyeOff className="h-2.5 w-2.5 mr-1" /> Hidden
                        </Badge>
                      )}
                      {comment.moderator_edited && (
                        <Badge variant="outline" className="rounded-full text-[10px] text-blue-700 border-blue-200">
                          <Shield className="h-2.5 w-2.5 mr-1" /> Mod-edited
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                    {comment.hidden && comment.hidden_reason && (
                      <p className="text-[11px] italic text-orange-700 mt-1">Hidden reason: {comment.hidden_reason}</p>
                    )}
                  </div>
                  <div className="flex gap-0.5 flex-shrink-0">
                    {user?.id !== comment.user_id && (
                      <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-muted-foreground hover:text-foreground" title="Report"
                        onClick={() => setReportTarget({ type: "comment", id: comment.id })}
                        data-testid={`report-comment-${comment.id}`}>
                        <Flag className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {isMod && (
                      <>
                        {comment.hidden ? (
                          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-primary" title="Unhide"
                            onClick={() => openModAction("comment", comment, "unhide")}
                            data-testid={`mod-unhide-comment-${comment.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-primary" title="Hide"
                            onClick={() => openModAction("comment", comment, "hide")}
                            data-testid={`mod-hide-comment-${comment.id}`}>
                            <EyeOff className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-primary" title="Edit (mod)"
                          onClick={() => openModAction("comment", comment, "edit")}
                          data-testid={`mod-edit-comment-${comment.id}`}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {(user?.id === comment.user_id || isMod) && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => isMod && user?.id !== comment.user_id
                          ? openModAction("comment", comment, "delete")
                          : handleDeleteComment(comment.id)}
                        className="rounded-full h-8 w-8 text-destructive hover:text-destructive"
                        title="Delete"
                        data-testid={`delete-comment-${comment.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}

            {/* Add Comment */}
            <form onSubmit={handleComment} className="flex gap-3 pt-4 border-t border-border">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                className="rounded-xl flex-1"
                data-testid="comment-input"
              />
              <Button type="submit" className="rounded-full" data-testid="submit-comment-btn">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
        {communityDialogs}
      </div>
    );
  }

  // Posts List View
  return (
    <div className="space-y-6" data-testid="community-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <MessageSquare className="h-8 w-8" />
            Community
          </h1>
          <p className="text-muted-foreground mt-1">Connect and share with other families</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="create-post-btn">
              <Plus className="mr-2 h-4 w-4" />
              New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create Post</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="What's on your mind?"
                  required
                  className="rounded-xl"
                  data-testid="post-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c} className="capitalize">
                        {c.replace("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Share your experience, ask questions, or offer support..."
                  required
                  className="rounded-xl resize-none"
                  rows={5}
                  data-testid="post-content-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" data-testid="submit-post-btn">
                  Post
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
          className="rounded-full"
        >
          All
        </Button>
        {CATEGORIES.map(cat => (
          <Button
            key={cat}
            variant={filter === cat ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(cat)}
            className="rounded-full capitalize"
          >
            {cat.replace("_", " ")}
          </Button>
        ))}
      </div>

      {/* Posts List */}
      {posts.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Posts Yet</h3>
            <p className="text-muted-foreground mb-6">Be the first to start a conversation!</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Create Post
            </Button>
          </CardContent>
        </Card>
      ) : filteredPosts.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No posts in this category</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post, i) => (
            <Card 
              key={post.id} 
              className={`border-0 shadow-lg rounded-3xl card-hover cursor-pointer animate-fadeIn stagger-${(i % 5) + 1}`}
              onClick={() => openPost(post)}
              data-testid={`post-card-${post.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <Avatar className="h-11 w-11 flex-shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {post.user_name?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-heading font-bold text-lg">{post.title}</h3>
                      <Badge className={`rounded-full text-xs capitalize ${getCategoryColor(post.category)}`}>
                        {post.category.replace("_", " ")}
                      </Badge>
                      {post.hidden && (
                        <Badge variant="outline" className="rounded-full text-[10px] text-orange-700 border-orange-300" data-testid={`hidden-badge-${post.id}`}>
                          <EyeOff className="h-2.5 w-2.5 mr-1" /> Hidden
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.content}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">{post.user_name}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(parseISO(post.created_at), "MMM d")}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <ReactionBar
                    post={post}
                    onReact={handleReaction}
                    onSave={handleSaveToLibrary}
                    commentCount={post.comment_count}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Report dialog (all users) */}
      {communityDialogs}
    </div>
  );
}
